import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../api/client";
import { useHashParams } from "../lib/useHashParams";
import { localLink } from "../lib/links";

interface DiffItem { path: string; title: string; deadline?: string; url: string }
interface Diff {
  run_date: string; state: string; complete: boolean; comparison_available: boolean;
  sections: Record<string, DiffItem[]>;
}
const labels: Record<string, string> = { new: "Neu", changed: "Geändert", archived: "Archiviert", upcoming: "Fristen" };

export function RunDiff({ runId, expanded = false }: { runId: string; expanded?: boolean }) {
  const [open, setOpen] = useState(false);
  if (expanded) return <DiffContent runId={runId} />;
  return <details className="run-diff-details" onToggle={(event) => setOpen(event.currentTarget.open)}>
    <summary>Änderungsbericht ansehen</summary>
    {open && <DiffContent runId={runId} />}
  </details>;
}

function DiffContent({ runId }: { runId: string }) {
  const report = useQuery<Diff>({ queryKey: ["run-report", runId], queryFn: () => apiGet(`/api/runs/${runId}/report`) });
  const section = useHashParams().get("section");
  useEffect(() => {
    if (report.data && section) document.getElementById(`result-${runId}-${section}`)?.scrollIntoView({ block: "nearest" });
  }, [report.data, runId, section]);
  if (report.isPending) return <p className="hint">Ergebnis wird geladen …</p>;
  if (report.isError) return <p role="alert">Ergebnis nicht verfügbar.</p>;
  const sections = Object.entries(report.data.sections).filter(([, items]) => items.length > 0);
  return <div className="run-result">
    <header className="run-result-header">
      <h2>Lauf vom {report.data.run_date}</h2>
      {!report.data.complete && <p>{report.data.state}</p>}
      {!report.data.comparison_available && <p>Ergebnisvergleich nicht verfügbar.</p>}
    </header>
    {sections.length === 0 && report.data.comparison_available && <p>Keine Änderungen.</p>}
    <div className="run-result-grid">
      {sections.map(([key, items]) => <section className="run-result-section" key={key} id={`result-${runId}-${key}`} aria-label={labels[key]}>
        <h3>{labels[key]} <span>{items.length}</span></h3>
        <ul>
          {items.map(item => <li key={item.path}>
            {item.deadline && <time>{item.deadline}</time>}
            <a href={localLink(item.url) ?? item.url}>{item.title}</a>
          </li>)}
        </ul>
      </section>)}
    </div>
  </div>;
}
